// The real self-capture flow, ported close to the archived in-house capture prototype:
// GDPR consent (+ language / paper-format pickers) → per-step instruction
// modal → rear-camera viewfinder with live ONNX guidance (foot/paper mask
// overlay, guidance banner, tilt-angle readout) and a per-step guide overlay →
// per-foot "foot still?" confirm → review → the unchanged presigned-PUT upload.
//
// Live guidance is best-effort: any failure (no wasm, model fetch error,
// jsdom) degrades to plain manual capture. The upload seam (`onComplete`) is
// untouched.

import { useEffect, useMemo, useRef, useState } from "react";

import {
  CAPTURE_STEPS,
  captureAngles,
  captureGravity,
  captureResolutionOk,
  armCrashGuard,
  captureResultsToInputs,
  crashNoticeDue,
  crashSafeMode,
  disarmCrashGuard,
  isOptionalUpload,
  captureSquareJpeg,
  captureFullFrameJpeg,
  releaseSnapshot,
  snapshotFrame,
  listCameraLabels,
  startCameraForFacing,
  startRearCamera,
  stopStream,
  streamInfo,
  trackRequestLabel,
  shutterEnabled,
  paperMetaInput,
  sensorCropFraction,
  type AngleName,
  type CaptureResult,
  type CaptureStep,
  type Side,
  type StreamInfo,
} from "../lib/capture";
import { collectDeviceInfo, type DeviceInfo } from "../lib/device";
import {
  MODEL_SIZE,
  analyzeOcclusion,
  angleFromBeta,
  angleOnlyMessage,
  angleReading,
  guidanceAdvisory,
  guidanceMessage,
  isLensBlocked,
  lensBlockingVerdict,
  isShadowAdvised,
  isSubstrateAdvised,
  occlusionStreak,
  newSessionTelemetry,
  recordSessionFrame,
  sessionTelemetry,
  SESSION_MAX_SAMPLES,
  SESSION_SAMPLE_EVERY,
  substrateTelemetry,
  summariseSubstrateWindow,
  SUBSTRATE_WINDOW_FRAMES,
  type AngleReading,
  type FrameAnalysis,
  type GuidanceMessage,
  type GuidanceState,
  type OcclusionAnalysis,
  type PaperShadowStats,
  type SubstrateSpread,
  type SubstrateStats,
  type SubstrateWindowSummary,
} from "../lib/guidance";
import type { GuidanceProvider, GuidanceSession } from "../lib/guidance-session";
import type { ScanSession } from "../lib/api";
import type { RunInfo } from "../lib/scan-runs";
import {
  APP_VERSION,
  DEBUG,
  devCaptureEnabled,
  diagEnabled,
  guidanceDisabledByUrl,
  guidanceProviders,
  isIOS,
  setDiagEnabled,
  storeCaptureLabel,
  storedCaptureLabel,
} from "../lib/config";
import { useI18n, type TransKey } from "../lib/i18n";
import { GravitySampler } from "../lib/gravity";
import type { FunnelEventType } from "../lib/telemetry";
import { versionedPublicUrl } from "../lib/public-asset-url";
import type { RunInput } from "../lib/upload-pipeline";
import { HezoLogo } from "./HezoLogo";
import { LanguageSwitcher } from "./LanguageSwitcher";

// Vendored from the footscan-training model registry (canonical `model.onnx`,
// shared with footscan-server) via scripts/vendor_models.sh — not committed.
// Versioned by content hash so a new model reaches browsers that cached the old one.
const GUIDANCE_MODEL_URL = versionedPublicUrl("/models/model.onnx");

const GUIDANCE_IN_TEST =
  import.meta.env.MODE === "test" || import.meta.env.TEST === true;

// Prototype cadence: a short pause after each shot before advancing. Zero under
// test so the component tests don't wait on real timers.
const POST_CAPTURE_DELAY_MS = GUIDANCE_IN_TEST ? 0 : 800;

// A freshly mounted modal ignores taps for this long. Without it the tail of a
// double-tap (on the shutter, or on the confirm dialog's answer) lands on the
// button of the card that appeared between the two taps and dismisses an
// instruction the customer never saw. Short enough to feel like nothing, long
// enough to swallow a stray tap. Deliberately NOT zeroed under test: the guard
// is CSS-only (`pointer-events`), which jsdom's fireEvent ignores, so the
// component tests drive the flow unchanged.
const MODAL_ARM_DELAY_MS = 350;

// How long the post-shutter white flash takes to fade. The frozen frame under
// it stays up for the whole POST_CAPTURE_DELAY_MS pause.
const SHOT_FLASH_MS = 200;

// Top of the square guide SVG inside the 4:3 band (75vmin high), chosen so the
// drawn span sits centred on the band's middle (37.5vmin). The side guides draw
// between 33% and 90% of their square (centre 61.7%), the top-down front guide
// between 22% and 82% (centre 52.3%); unshifted, the sheet's edge left the band.
const IBV_GUIDE_TOP_VMIN: Record<AngleName, number> = {
  inside: 37.5 - 61.7,
  front: 37.5 - 52.3,
  outside: 37.5 - 61.7,
};

// Cap live-guidance inference well below the display refresh. The banner +
// overlay don't need 60 fps, and running a full 128×128 segmentation
// back-to-back (as the prototype's rAF loop did) pegs mobile GPUs/thermals hard
// enough that the browser jettisons the tab — the "page just reloads" symptom.
// ~9 fps stays responsive while cutting sustained load (and per-frame
// allocations) roughly 6×.
//
// The floor alone only fits devices where inference is quick. A phone on the
// wasm fallback needs a few hundred ms per frame, and chaining those with only
// the floor between them keeps a core saturated for the whole capture — heat,
// battery, and eventually the throttling that makes guidance worse anyway. So
// the pause after each frame follows the measured inference time (half of it,
// inside these bounds): fast devices keep their cadence, slow ones idle between
// frames instead of grinding.
const GUIDANCE_MIN_INTERVAL_MS = 110;
const GUIDANCE_MAX_INTERVAL_MS = 400;

// Consecutive failed inferences that count as "this EP cannot run the model"
// rather than a transient frame hiccup. WebGPU compiles its shaders lazily, per
// op, so a device whose driver chokes on the model creates the session happily
// and then throws on every single frame — a dead overlay with no banner. At
// that point the loop rebuilds once on wasm.
const GUIDANCE_MAX_FRAME_ERRORS = 3;

export type GuidanceStatus = "off" | "loading" | "on" | "unavailable";

/**
 * Paper reference formats. The operator picks one; the choice is propagated to
 * the backend via `capture-meta.json` (see `paperMetaInput`). footscan-server's
 * pose still assumes A4 today — Letter handling (and Letter guide overlays) is a
 * backend follow-up.
 */
const PAPER_FORMATS = ["A4", "Letter"] as const;
export type PaperFormat = (typeof PAPER_FORMATS)[number];

// Dark theme classes, matching App.tsx + the immersive viewfinder.
const PANEL = "rounded-xl border border-white/10 bg-white/5 p-4";
const PRIMARY_BTN =
  "w-full rounded-full bg-white py-3.5 text-base font-semibold text-slate-900 shadow-lg transition active:bg-slate-100 disabled:opacity-40";
const SECONDARY_BTN =
  "w-full rounded-full border border-white/15 bg-transparent py-3 text-sm font-medium text-slate-200 transition hover:bg-white/5";

// Keep captured frames in the canonical CAPTURE_STEPS order regardless of the
// order they were actually shot in. This matters once a single foot can be
// re-taken out of sequence (from the review screen): the upload filename order,
// the review thumbnails, and the "first un-captured step" derivation all assume
// canonical order. Step objects are the shared CAPTURE_STEPS references, so
// indexOf identifies them.
function sortByStep(rs: readonly CaptureResult[]): CaptureResult[] {
  return [...rs].sort(
    (a, b) => CAPTURE_STEPS.indexOf(a.step) - CAPTURE_STEPS.indexOf(b.step),
  );
}

// A snapshot of the live camera + platform facts for the on-device diag panel.
// Collected at discrete moments (camera acquired / switched / diag toggled on),
// NOT per frame. Screenshot-friendly: the tester flips diag on and photographs
// the overlay instead of tethering to a dev console.
/** How the live-guidance session resolved: which EP took it, or why none did. */
export interface GuidanceOutcome {
  provider?: GuidanceProvider;
  error?: string;
  /** Smoothed inference time in ms — how fast this device actually is. */
  inferMs?: number;
}

export interface CameraDiagSnapshot {
  info: StreamInfo;
  /** What the track asked for (`?track=` override or the 1280×720 default). */
  trackRequest: string;
  /** size/width of the centre-square crop for the current frame (or null). */
  cropFraction: number | null;
  /** Every video-input label the device exposes (all lenses, not just active). */
  lenses: string[];
  providers: string;
  ios: boolean;
  webgpu: boolean;
  ua: string;
}

function hasWebGPU(): boolean {
  return "gpu" in navigator && (navigator as { gpu?: unknown }).gpu != null;
}

// One substrate signal's rolling spread as "p05·p50·p95", for the diag panel's
// calibration line. `scale` renders 0–1 fractions as whole percent.
function spreadText(s: SubstrateSpread, digits: number, scale = 1): string {
  const f = (v: number): string => (v * scale).toFixed(digits);
  return `${f(s.p05)}·${f(s.p50)}·${f(s.p95)}`;
}

/** A 0–1 rate as a whole-percent string. */
function rateText(r: number): string {
  return `${Math.round(r * 100)}%`;
}

// Flatten a snapshot into the plain object embedded in capture-meta.json's diag
// field (only when diag is on). Mirrors the on-screen panel so a screenshot and
// the uploaded sidecar carry the same facts.
function diagMetaFrom(
  snap: CameraDiagSnapshot,
  guidance: GuidanceOutcome = {},
): Record<string, unknown> {
  return {
    version: APP_VERSION,
    lens: snap.info.label,
    facingMode: snap.info.facingMode,
    deviceId: snap.info.deviceId,
    width: snap.info.width,
    height: snap.info.height,
    zoom: snap.info.zoom ?? null,
    trackRequest: snap.trackRequest,
    cropFraction: snap.cropFraction,
    lenses: snap.lenses,
    providers: snap.providers,
    ios: snap.ios,
    webgpu: snap.webgpu,
    guidanceProvider: guidance.provider ?? null,
    guidanceInferMs: guidance.inferMs ?? null,
    guidanceError: guidance.error ?? null,
    ua: snap.ua,
  };
}

// Best-effort iOS 13+ deviceorientation + devicemotion permission, must be
// triggered from a user gesture (the consent "start" tap). Denied → no angle
// readout and no gravity vector. Both calls start synchronously, because iOS
// ties each to the tap and an await in between would lose it.
async function maybeRequestOrientationPermission(): Promise<void> {
  type Requestable = { requestPermission?: () => Promise<string> };
  const g = globalThis as { DeviceOrientationEvent?: Requestable; DeviceMotionEvent?: Requestable };
  const pending: Array<Promise<string>> = [];
  for (const api of [g.DeviceOrientationEvent, g.DeviceMotionEvent]) {
    if (api && typeof api.requestPermission === "function") {
      try {
        pending.push(api.requestPermission());
      } catch {
        /* thrown synchronously outside a gesture — non-fatal */
      }
    }
  }
  // Denied / dismissed — non-fatal either way.
  await Promise.allSettled(pending);
}

type CapturePhase =
  | "consent"
  | "prep"
  | "resume"
  | "starting"
  | "denied"
  | "capturing"
  | "review";
// Overlays shown on top of the live viewfinder (camera stays running under
// them, like the prototype's modals).
type ModalKind = null | "instruction" | "confirm";

export function CaptureFlow({
  session,
  onComplete,
  onSimulate,
  onFunnelEvent,
  run,
  consented,
  onConsent,
  startMode,
}: {
  session: ScanSession;
  /** `label` is the dev-capture note (see `captureLabel` below); unused on the
   *  real upload path, where the scanID already identifies the run. */
  onComplete: (inputs: RunInput[], label?: string) => void;
  /** DEBUG-only: drive the chain with synthetic bytes (no camera). Takes the
   *  label too, so the simulate path cannot silently drop it. */
  onSimulate?: (label?: string) => void;
  /** Campaign funnel telemetry for the three steps that only this component can
   *  see (consent, camera start, all 6 shots). Fire-and-forget: the caller may
   *  not await it, and it must never be allowed to break the capture flow.
   *  Optional so the component stays usable without any telemetry wiring. */
  onFunnelEvent?: (type: FunnelEventType, detail?: Record<string, unknown>) => void;
  /** Scan run of a multi-run invite ("Durchlauf k von N"), shown in the
   *  viewfinder. Null or absent for a single-run scan. */
  run?: RunInfo | null;
  /** Set from the second run on: consent was given in run 1, so the flow
   *  opens at the prep checklist with the same paper format. */
  consented?: { paperFormat: PaperFormat } | null;
  /** Consent given, with the paper format chosen on that screen. */
  onConsent?: (paperFormat: PaperFormat) => void;
  /** With `consented`, from run 2 on: skip the prep checklist too. "auto"
   *  starts the camera straight away (same visit, the motion permission from
   *  run 1's tap still holds); "tap" shows one short screen whose button is
   *  the tap iOS needs for that permission (a reopened link). */
  startMode?: "auto" | "tap";
}): React.ReactElement {
  const [phase, setPhase] = useState<CapturePhase>(
    !consented ? "consent" : startMode === "tap" ? "resume" : startMode === "auto" ? "starting" : "prep",
  );
  const [modal, setModal] = useState<ModalKind>(null);
  const [agreed, setAgreed] = useState(Boolean(consented));
  const [paperFormat, setPaperFormat] = useState<PaperFormat>(consented?.paperFormat ?? "A4");
  const [results, setResults] = useState<CaptureResult[]>([]);
  // Non-null while the customer is re-taking ONE foot from the review screen
  // (its 3 angles were dropped and are being recaptured). Null during the normal
  // linear first pass. Gates the post-capture routing so a retake returns to
  // review instead of running the per-foot "still?" confirm again.
  const [retakeSide, setRetakeSide] = useState<Side | null>(null);
  // True while a shot is being taken + during the post-capture pause, so the
  // shutter can't double-fire.
  const [busy, setBusy] = useState(false);
  // True from the shutter until the shot's JPEGs are encoded; the guidance
  // loop reads it to pause (a ref, so the loop's effect does not restart).
  const encodingRef = useRef(false);
  // Object URL of the frame just taken, shown frozen over the viewfinder for
  // the post-capture pause. This is the shutter's only "the photo is in the
  // box" feedback (with a flash and, where supported, a short haptic): before
  // it, a tap produced no visible change at all and the next card appearing
  // 800 ms later read as the app jumping ahead on its own.
  const [shotPreview, setShotPreview] = useState<string | null>(null);
  // Mirror of `shotPreview` so the unmount teardown can revoke a URL that is
  // still showing.
  const shotPreviewRef = useRef<string | null>(null);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  // True once the live stream reports a short-side below MIN_CAPTURE_DIMENSION —
  // gates the shutter and shows a warning, so a too-coarse frame can't be
  // silently captured and uploaded (issue #56). Re-evaluated on every stream
  // (re)attach via the video's loadedmetadata.
  const [lowResolution, setLowResolution] = useState(false);
  // On-device diagnostics: reactive so the panel appears the moment it's toggled
  // (config's diagEnabled seeds it from DEBUG / ?diag=1 / persisted localStorage).
  const [diag, setDiag] = useState<boolean>(() => diagEnabled());
  const [cameraDiag, setCameraDiag] = useState<CameraDiagSnapshot | null>(null);
  // Dev-capture only: a free-text note (whose foot, which run) that rides along
  // to the capture-ingest and groups this run with the same foot captured in
  // tools/proto-ab. Pre-filled from the last run — a capture session is several
  // runs of one foot, and re-typing the note is how it ends up inconsistent.
  const devCapture = devCaptureEnabled();
  const [captureLabel, setCaptureLabel] = useState<string>(() =>
    devCapture ? storedCaptureLabel() : "",
  );

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // Callback ref rather than a phase-keyed effect: the <video> element is
  // remounted whenever the viewfinder subtree comes back (answering "no, the
  // foot moved" after the 6th shot does exactly that) and `phase` does not
  // change across such a remount, so the old effect never re-attached the
  // stream. That left a black viewfinder whose every shutter tap threw "camera
  // frame not ready yet", with no way out but a reload. Attaching from the ref
  // covers every mount by construction.
  const attachVideo = (el: HTMLVideoElement | null): void => {
    videoRef.current = el;
    if (!el || !streamRef.current) return;
    if (el.srcObject !== streamRef.current) el.srcObject = streamRef.current;
    void el.play()?.catch(() => {
      /* autoplay can reject silently; the frame still shows */
    });
  };
  // Crop fraction of the actual capture frame — recorded at shot time (the
  // stream is stopped before upload). Rides to the backend in capture-meta.json
  // so the focal-length pose is correct on any frame aspect (footscan-server #45).
  const cropFractionRef = useRef<number | null>(null);
  // Phone + rear-lens facts for the sidecar's `device` block. Recorded whenever
  // a camera starts, because `getSettings()`/`getCapabilities()` go empty once
  // the track stops — and the flow stops it before the review screen.
  const deviceInfoRef = useRef<DeviceInfo | null>(null);
  // Gravity vector per shot for footscan-server's IBV step.
  const gravitySamplerRef = useRef(new GravitySampler());

  // Manual camera picker: iOS often ignores the rear-lens request and opens the
  // front camera, so the viewfinder always offers a switch. We track the active
  // deviceId to cycle from, and the last facingMode for the fallback toggle.
  const currentDeviceIdRef = useRef<string | null>(null);
  const facingRef = useRef<"user" | "environment">("environment");

  // Hidden diag toggle: 3 quick taps on the viewfinder logo flip diag mode (no
  // cable, no URL param). A ref-counter avoids re-render churn per tap.
  const diagRef = useRef(diag);
  diagRef.current = diag;
  const logoTapsRef = useRef(0);
  const logoTapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Snapshot the active lens + all enumerated lenses + platform into the diag
  // panel/metadata. Called at discrete moments only (never per frame).
  async function refreshCameraDiag(): Promise<void> {
    const info = streamInfo(streamRef.current);
    let lenses: string[] = [];
    try {
      lenses = await listCameraLabels();
    } catch {
      /* enumerate unavailable — leave lenses empty */
    }
    const cropFraction =
      info.width && info.height ? sensorCropFraction(info.width, info.height) : null;
    setCameraDiag({
      info,
      trackRequest: trackRequestLabel(),
      cropFraction,
      lenses,
      providers: guidanceProviders().join(","),
      ios: isIOS(),
      webgpu: hasWebGPU(),
      ua: navigator.userAgent,
    });
  }

  // Best-effort and deliberately un-awaited by the callers: a phone that
  // answers nothing (or slowly) must not delay the viewfinder.
  async function recordDeviceInfo(): Promise<void> {
    try {
      deviceInfoRef.current = await collectDeviceInfo(streamRef.current);
    } catch {
      /* leave whatever we had; the sidecar simply omits the block */
    }
  }

  function setDiagMode(on: boolean): void {
    setDiag(on);
    setDiagEnabled(on);
    if (on) void refreshCameraDiag();
  }

  function onLogoTap(): void {
    logoTapsRef.current += 1;
    if (logoTapTimerRef.current) clearTimeout(logoTapTimerRef.current);
    logoTapTimerRef.current = setTimeout(() => {
      logoTapsRef.current = 0;
    }, 600);
    if (logoTapsRef.current >= 3) {
      logoTapsRef.current = 0;
      setDiagMode(!diagRef.current);
    }
  }

  // Next step = the first CAPTURE_STEPS entry with no result yet. During the
  // normal first pass (results filled in order) this equals CAPTURE_STEPS[
  // results.length], so the linear flow is unchanged; when one foot's shots
  // have been dropped for a retake it correctly points back at that foot's gap.
  const capturedIds = useMemo(() => new Set(results.map((r) => r.step.id)), [results]);
  const currentStep = CAPTURE_STEPS.find((s) => !capturedIds.has(s.id));
  const stepIndex = currentStep ? CAPTURE_STEPS.indexOf(currentStep) : CAPTURE_STEPS.length;
  // What the viewfinder renders. Once all six shots are in there is no next
  // step, but the "foot still?" confirm is still to be answered and that answer
  // can send the customer back into the viewfinder. Falling back to the last
  // step keeps the camera view mounted under the dialog instead of blanking the
  // screen for the length of the pause.
  const viewStep = currentStep ?? CAPTURE_STEPS[CAPTURE_STEPS.length - 1];
  const viewStepNumber = CAPTURE_STEPS.indexOf(viewStep) + 1;

  // Funnel step 4: the full set is in the box. Written as an effect rather than
  // at the two call sites that reach review (the linear "foot still? → yes" and
  // the retake completing a foot), so both are covered by one condition and
  // neither can drift out of it.
  const shotCount = results.length;
  useEffect(() => {
    if (phase !== "review" || shotCount !== CAPTURE_STEPS.length) return;
    onFunnelEvent?.("shots_complete", { shots: shotCount });
  }, [phase, shotCount, onFunnelEvent]);

  // ── Live-guidance state (optional; the flow works without it) ──────────────
  const [guidanceStatus, setGuidanceStatus] = useState<GuidanceStatus>("off");
  const [guidanceState, setGuidanceState] = useState<GuidanceState | null>(null);
  // What the guidance session actually did on THIS device: the EP it ended up
  // running on (wasm when WebGPU was requested but fell back) or, when nothing
  // worked, the runtime's own error text. Requesting WebGPU and getting a bare
  // "model could not be loaded" banner is unfixable from a screenshot, so both
  // go into the diag panel and the capture-meta sidecar.
  const [guidanceOutcome, setGuidanceOutcome] = useState<GuidanceOutcome>({});
  // Mirror for the sidecar builders, which run outside the guidance effect.
  const guidanceOutcomeRef = useRef<GuidanceOutcome>(guidanceOutcome);
  guidanceOutcomeRef.current = guidanceOutcome;
  // Raw camera angle; the reading against the current photo's window is derived
  // per render, so the window follows the step without re-subscribing.
  const [angleValue, setAngleValue] = useState<number | null>(null);
  const angle: AngleReading | null =
    angleValue !== null && currentStep ? angleReading(angleValue, currentStep.photoIndex) : null;
  // Mirror of `angle` for the guidance loop, which recomputes the banner for the
  // session record and must not close over a stale render's value.
  const angleRef = useRef<AngleReading | null>(angle);
  angleRef.current = angle;
  // Lens-occlusion: streak counter lives in a ref (per-frame, no re-render);
  // `lensBlocked` only flips state at the debounced edges, and the raw luma
  // stats are mirrored into state solely while the diag panel is showing.
  const [lensBlocked, setLensBlocked] = useState(false);
  const [lensStats, setLensStats] = useState<OcclusionAnalysis | null>(null);
  const occlusionStreakRef = useRef(0);
  // Paper-shadow advisory (non-blocking): same debounced-streak shape as the
  // lens check. `lightingAdvised` flips the second banner slot; the raw shadow
  // stats are mirrored into state only while the diag panel is showing.
  const [lightingAdvised, setLightingAdvised] = useState(false);
  const [shadowStats, setShadowStats] = useState<PaperShadowStats | null>(null);
  const shadowStreakRef = useRef(0);
  // Substrate (floor) interference advisories (non-blocking): two independent
  // debounced streaks — the sheet blending into the floor (low contrast) and a
  // busy/patterned floor confusing the mask — each flipping the second banner
  // slot. Raw stats mirror into state only while the diag panel is showing.
  const [lowContrastAdvised, setLowContrastAdvised] = useState(false);
  const [busySurfaceAdvised, setBusySurfaceAdvised] = useState(false);
  const [substrateStats, setSubstrateStats] = useState<SubstrateStats | null>(null);
  const contrastStreakRef = useRef(0);
  const busyStreakRef = useRef(0);
  // Rolling window of substrate samples behind the diag panel's spread +
  // fire-rate readout. Calibration aid only — it never feeds a verdict — so it
  // fills exclusively while diag is on and is dropped when the loop tears down.
  const substrateWindowRef = useRef<SubstrateStats[]>([]);
  // Window summary frozen at each shutter, keyed by upload filename — the
  // sidecar's substrate telemetry. Kept off CaptureResult on purpose: this is
  // diag data about the FLOOR, not capture data the backend consumes. Keying by
  // filename makes a retake overwrite its own entry.
  const substrateAtShotRef = useRef<Record<string, SubstrateWindowSummary>>({});
  // Whole-session record, independent of whether a shot is ever taken: the
  // banner histogram (what blocked the shutter, and for how long) plus a
  // decimated substrate trail. On a floor guidance never clears there IS no
  // shot and no upload, so without this the worst surfaces report nothing.
  const sessionAccRef = useRef(newSessionTelemetry());
  const sessionTrailRef = useRef<SubstrateStats[]>([]);
  const [telemetrySaved, setTelemetrySaved] = useState(false);
  const [substrateSummary, setSubstrateSummary] = useState<SubstrateWindowSummary | null>(null);

  const overlayRef = useRef<HTMLCanvasElement | null>(null);
  const expectedSideRef = useRef<Side>("left");
  expectedSideRef.current = viewStep.side;

  const guidanceEnabled = !GUIDANCE_IN_TEST && !guidanceDisabledByUrl() && !crashSafeMode();
  // Suppressed while the frozen frame is up: guidance keeps running on the live
  // frames underneath, so leaving it on flashes "no paper in view" over a shot
  // that was just taken fine, while the customer is already lowering the phone.
  // Without a running model (`?ai=none`, loading, unavailable) the tilt alone
  // still drives the banner and gates the shutter: the angle window matters to
  // both solvers, and a phone too weak for the model needs it most.
  const banner: GuidanceMessage | null =
    shotPreview !== null
      ? null
      : guidanceStatus === "on"
        ? guidanceState
          ? guidanceMessage(guidanceState, angle, lensBlocked)
          : null
        : angleOnlyMessage(angle);

  // Non-blocking second banner. Only surfaced when the primary banner is "good":
  // a lighting/substrate hint is noise while the customer still has a blocking
  // problem (no paper, wrong foot, bad frame), and these stats are meaningless
  // without a framed sheet anyway. At most one advisory shows, priority-ordered
  // inside guidanceAdvisory.
  const advisory: GuidanceMessage | null =
    banner?.severity === "good"
      ? guidanceAdvisory({
          lightingUneven: lightingAdvised,
          lowPaperContrast: lowContrastAdvised,
          busySurface: busySurfaceAdvised,
        })
      : null;

  // The live stream is attached by `attachVideo` (the <video> callback ref), so
  // every mount of the viewfinder picks it up. Teardown stays here.
  useEffect(() => {
    return () => {
      stopStream(streamRef.current);
      if (shotPreviewRef.current) URL.revokeObjectURL(shotPreviewRef.current);
    };
  }, []);

  // Gravity vector for the IBV step, sampled continuously so the shutter can
  // read a smoothed value without waiting for an event.
  useEffect(() => {
    if (phase !== "capturing") return;
    const sampler = gravitySamplerRef.current;
    const onMotion = (e: DeviceMotionEvent): void => {
      const a = e.accelerationIncludingGravity;
      sampler.push(a?.x, a?.y, a?.z);
    };
    window.addEventListener("devicemotion", onMotion);
    return () => {
      window.removeEventListener("devicemotion", onMotion);
      sampler.clear();
    };
  }, [phase]);

  // Tilt readout from the phone's orientation sensor (prototype: 90 − round(beta)).
  // Crash guard (see crashSafeMode): armed while the camera runs, disarmed on
  // every orderly exit. A tab Safari kills fires none of these, so its mark
  // survives into the reload. Re-armed when the tab comes back.
  useEffect(() => {
    if (phase !== "capturing") return;
    armCrashGuard();
    const onVisibility = (): void => {
      if (document.visibilityState === "hidden") disarmCrashGuard();
      else armCrashGuard();
    };
    const onPageHide = (): void => disarmCrashGuard();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      disarmCrashGuard();
    };
  }, [phase]);

  // Independent of the AI guidance: the angle gates the shutter either way.
  useEffect(() => {
    if (phase !== "capturing") return;
    const onOrient = (e: DeviceOrientationEvent): void => {
      if (e.beta == null) return;
      setAngleValue(angleFromBeta(e.beta));
    };
    window.addEventListener("deviceorientation", onOrient);
    return () => {
      window.removeEventListener("deviceorientation", onOrient);
      setAngleValue(null);
    };
  }, [phase]);

  // Per-frame ONNX inference on the live viewfinder (best-effort).
  useEffect(() => {
    if (phase !== "capturing" || !guidanceEnabled) {
      setGuidanceStatus("off");
      return;
    }

    let cancelled = false;
    let raf = 0;
    let lastRun = 0;
    let frameCount = 0;
    let frameErrors = 0;
    // Smoothed inference time, and with it the loop's own cadence.
    let inferMs = 0;
    let downgraded = false;
    let session2: GuidanceSession | null = null;

    // Platform-aware EP selection: iOS → wasm-only (WebGPU crashes the tab
    // there), else WebGPU→wasm. `?ep=` overrides for on-device testing.
    // Diagnostics (silent unless ?diag=1 / DEBUG): selected EP + frame heartbeat
    // so a live Web Inspector session shows how far the loop got before a crash.
    const diag = diagEnabled();
    const providers = guidanceProviders();

    const off = document.createElement("canvas");
    off.width = MODEL_SIZE;
    off.height = MODEL_SIZE;
    const offCtx = off.getContext("2d", { willReadFrequently: true });

    function drawOverlay(a: FrameAnalysis): void {
      const canvas = overlayRef.current;
      const ctx = canvas?.getContext("2d");
      if (!canvas || !ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = a.state.wrongFoot ? "#ff4d4d" : "#00ff00";
      for (const p of a.footEdges) ctx.fillRect(p.x, p.y, 1, 1);
      const paperBad =
        a.state.paperTooLarge || a.state.paperTooSmall || a.state.paperTooCloseToEdge;
      ctx.fillStyle = paperBad ? "#ff4d4d" : "#00ff00";
      for (const p of a.paperEdges) ctx.fillRect(p.x, p.y, 1, 1);
    }

    // Throttled via the rAF timestamp: rAF still fires every frame (so the loop
    // pauses cleanly when the tab is hidden) but we only run inference once the
    // min interval has elapsed. `ts` is undefined on the initial kickoff, which
    // just skips one frame before the first inference.
    async function step(ts?: number): Promise<void> {
      if (cancelled) return;
      const pause = Math.min(
        GUIDANCE_MAX_INTERVAL_MS,
        Math.max(GUIDANCE_MIN_INTERVAL_MS, inferMs / 2),
      );
      // Paused while a shot encodes: the JPEGs and the inference would fight
      // over the same slow GPU/CPU and stretch the shutter delay.
      if (ts === undefined || ts - lastRun < pause || encodingRef.current) {
        if (!cancelled) raf = requestAnimationFrame((t) => void step(t));
        return;
      }
      const video = videoRef.current;
      try {
        if (video && session2 && offCtx && video.videoWidth && video.videoHeight) {
          const vw = video.videoWidth;
          const vh = video.videoHeight;
          const sq = Math.min(vw, vh);
          offCtx.drawImage(
            video,
            (vw - sq) / 2,
            (vh - sq) / 2,
            sq,
            sq,
            0,
            0,
            MODEL_SIZE,
            MODEL_SIZE,
          );
          const frame = offCtx.getImageData(0, 0, MODEL_SIZE, MODEL_SIZE);
          // Lens-occlusion check on the raw frame BEFORE inference — a covered
          // lens produces garbage segmentation, so the verdict must not depend
          // on the model. Debounced via the streak so a passing hand won't warn.
          const occl = analyzeOcclusion(frame.data);
          occlusionStreakRef.current = occlusionStreak(
            occlusionStreakRef.current,
            lensBlockingVerdict(occl),
          );
          const blocked = isLensBlocked(occlusionStreakRef.current);
          setLensBlocked(blocked);
          if (diagRef.current) setLensStats(occl);
          const startedAt = performance.now();
          // Advisories (uneven light, floor contrast, busy surface) are
          // diag-only: they never block, they were misfiring more than they
          // helped, and they are the third of the per-frame analysis a phone on
          // the wasm fallback cannot spare. Off, `analyzeFrame` skips both
          // extra passes over the raw frame entirely.
          const analysis = await session2.analyze(
            frame,
            expectedSideRef.current,
            diagRef.current,
          );
          if (cancelled) return;
          frameErrors = 0;
          // Exponential average: one slow frame (a GC pause, a backgrounded
          // tab) must not halve the cadence for the rest of the session.
          const took = performance.now() - startedAt;
          inferMs = inferMs ? inferMs * 0.7 + took * 0.3 : took;
          drawOverlay(analysis);
          setGuidanceState(analysis.state);
          // Paper-shadow advisory: debounce the uneven-lighting verdict the same
          // way as the lens check so the second banner doesn't flicker.
          shadowStreakRef.current = occlusionStreak(
            shadowStreakRef.current,
            analysis.paperShadow?.uneven ?? false,
          );
          setLightingAdvised(isShadowAdvised(shadowStreakRef.current));
          if (diagRef.current) setShadowStats(analysis.paperShadow);
          // Substrate (floor) interference: two independent debounced verdicts,
          // same streak shape so neither advisory flickers.
          const sub = analysis.substrate;
          contrastStreakRef.current = occlusionStreak(
            contrastStreakRef.current,
            sub?.lowContrast ?? false,
          );
          busyStreakRef.current = occlusionStreak(busyStreakRef.current, sub?.busy ?? false);
          setLowContrastAdvised(isSubstrateAdvised(contrastStreakRef.current));
          setBusySurfaceAdvised(isSubstrateAdvised(busyStreakRef.current));
          if (diagRef.current) setSubstrateStats(sub);
          // Session record. Recomputing the banner here (rather than reading the
          // rendered one) keeps the count on the loop's own cadence — one entry
          // per analysed frame, no React batching in between.
          recordSessionFrame(
            sessionAccRef.current,
            guidanceMessage(analysis.state, angleRef.current, blocked),
          );
          frameCount++;
          // Rolling calibration window. The window itself fills on every frame
          // (one small object per frame) so a shutter can always snapshot it —
          // including with the panel collapsed. Only the PANEL summary is
          // diag-gated and throttled: the spread barely moves between
          // neighbouring frames, and six sorts per frame would be paid on the
          // same budget as the guidance inference itself.
          if (sub) {
            const win = substrateWindowRef.current;
            win.push(sub);
            if (win.length > SUBSTRATE_WINDOW_FRAMES) win.shift();
            if (diagRef.current && frameCount % 10 === 0) {
              setSubstrateSummary(summariseSubstrateWindow(win));
            }
            // Decimated session trail — one sample per SESSION_SAMPLE_EVERY
            // frames, capped, so a long unshootable session stays bounded.
            if (frameCount % SESSION_SAMPLE_EVERY === 0) {
              const trail = sessionTrailRef.current;
              trail.push(sub);
              if (trail.length > SESSION_MAX_SAMPLES) trail.shift();
            }
          }
          if (diag && frameCount % 10 === 0) {
            const ms = Math.round(inferMs);
            setGuidanceOutcome((o) => (o.inferMs === ms ? o : { ...o, inferMs: ms }));
          }
          if (diag && frameCount % 30 === 0) {
            console.log(
              `[guidance] frame ${frameCount} · video ${vw}×${vh} · ep ${providers.join(",")} · ${Math.round(inferMs)}ms`,
            );
          }
        }
      } catch (err) {
        // Normally a transient frame error — keep looping. But this catch has
        // been silently swallowing errors, which could be hiding the real
        // WebGPU failure; surface it under diagnostics.
        if (diag) console.warn("[guidance] frame error", err);
        frameErrors++;
        if (
          !downgraded &&
          frameErrors >= GUIDANCE_MAX_FRAME_ERRORS &&
          session2?.provider === "webgpu"
        ) {
          downgraded = true;
          await rebuildOnWasm(err);
        }
      }
      // Measured from the END of the frame: the pause above is idle time the
      // device gets back, not a deadline the inference has already eaten.
      lastRun = performance.now();
      if (!cancelled) raf = requestAnimationFrame((t) => void step(t));
    }

    // Swap a WebGPU session that runs but produces nothing for a wasm one.
    // Slower, but it is the difference between guidance and a dead overlay.
    async function rebuildOnWasm(cause: unknown): Promise<void> {
      const dead = session2;
      session2 = null;
      if (dead) {
        try {
          await dead.close();
        } catch {
          /* releasing a broken session is best-effort */
        }
      }
      if (cancelled) return;
      try {
        const mod = await import("../lib/guidance-session");
        const next = await mod.createGuidanceSession(GUIDANCE_MODEL_URL, ["wasm"]);
        if (cancelled) {
          void next.close();
          return;
        }
        session2 = next;
        setGuidanceOutcome({
          provider: next.provider,
          error: `webgpu frames failed: ${cause instanceof Error ? cause.message : String(cause)}`,
        });
      } catch (err) {
        if (cancelled) return;
        // Nothing left to fall back to — stop the loop and tell the customer
        // they are on manual capture instead of spinning a dead rAF.
        cancelled = true;
        setGuidanceOutcome({ error: err instanceof Error ? err.message : String(err) });
        setGuidanceStatus("unavailable");
      }
    }

    setGuidanceStatus("loading");
    void (async () => {
      try {
        const mod = await import("../lib/guidance-session");
        if (cancelled) return;
        if (diag) {
          const hasWebGPU = "gpu" in navigator && navigator.gpu != null;
          console.log(`[guidance] init · requested ep ${providers.join(",")} · navigator.gpu ${hasWebGPU}`);
        }
        session2 = await mod.createGuidanceSession(GUIDANCE_MODEL_URL, providers);
        if (cancelled) {
          void session2.close();
          session2 = null;
          return;
        }
        setGuidanceOutcome({ provider: session2.provider });
        setGuidanceStatus("on");
        void step();
      } catch (err) {
        if (diag) console.warn("[guidance] session init failed", err);
        if (!cancelled) {
          setGuidanceOutcome({ error: err instanceof Error ? err.message : String(err) });
          setGuidanceStatus("unavailable");
        }
      }
    })();

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      const s = session2;
      session2 = null;
      if (s) void s.close();
      setGuidanceState(null);
      occlusionStreakRef.current = 0;
      setLensBlocked(false);
      setLensStats(null);
      shadowStreakRef.current = 0;
      setLightingAdvised(false);
      setShadowStats(null);
      contrastStreakRef.current = 0;
      busyStreakRef.current = 0;
      setLowContrastAdvised(false);
      setBusySurfaceAdvised(false);
      setSubstrateStats(null);
      substrateWindowRef.current = [];
      setSubstrateSummary(null);
    };
  }, [phase, guidanceEnabled]);

  // Re-check the delivered resolution whenever a stream (re)attaches. Fed by the
  // video's loadedmetadata, so it reflects the post-applyConstraints size and a
  // manual camera switch alike.
  function evaluateResolution(): void {
    const video = videoRef.current;
    if (!video?.videoWidth || !video.videoHeight) return;
    setLowResolution(!captureResolutionOk(video.videoWidth, video.videoHeight));
    if (diagRef.current) void refreshCameraDiag();
  }

  // The consent screen's "continue" tap. Split from `requestCamera` because
  // that is also the retake entry point: consent is given exactly once per
  // flow, when the checkbox is ticked and this button is pressed. The camera
  // starts from the prep checklist's button, so iOS still sees the orientation
  // permission request as the direct result of a tap.
  function startFromConsent(): void {
    // Funnel step 2.
    onFunnelEvent?.("consent_accepted");
    onConsent?.(paperFormat);
    setPhase("prep");
    // The consent page is long; the checklist must start at its heading.
    window.scrollTo?.(0, 0);
  }

  async function requestCamera(): Promise<void> {
    setStartError(null);
    setLowResolution(false);
    setPhase("starting");
    await maybeRequestOrientationPermission();
    try {
      const stream = await startRearCamera();
      streamRef.current = stream;
      const settings = stream.getVideoTracks()[0]?.getSettings();
      currentDeviceIdRef.current = settings?.deviceId ?? null;
      if (settings?.facingMode === "user" || settings?.facingMode === "environment") {
        facingRef.current = settings.facingMode;
      }
      void recordDeviceInfo();
      if (diagRef.current) void refreshCameraDiag();
      // Funnel step 3: the camera is actually live. Deliberately after
      // getUserMedia resolved: a permission denial must not count as a started
      // capture. Retakes route through here again; the caller de-duplicates.
      onFunnelEvent?.("capture_started");
      setPhase("capturing");
      setModal("instruction"); // prototype shows the step card before each step
    } catch (err) {
      setStartError((err as Error).message);
      setPhase("denied");
    }
  }

  // Run 2+ of the same visit: no checklist, the camera starts on its own.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (!consented || startMode !== "auto" || autoStartedRef.current) return;
    autoStartedRef.current = true;
    void requestCamera();
    // Mount-only: the run's flow starts once.
  }, []);

  // Flip between the front and back camera. This is a true front⇄back TOGGLE,
  // not a device-index cycle: modern phones enumerate several rear lenses (main
  // / ultra-wide / telephoto), so the old `(idx + 1) % count` cycle stepped onto
  // another BACK camera before ever reaching the selfie lens ("two taps to get
  // the front camera"). startCameraForFacing selects by localised label and, for
  // the back target, prefers the main lens over the ultra-wide/telephoto. The
  // button is always shown (see the viewfinder) — never gated on a device count.
  async function switchCamera(): Promise<void> {
    setCaptureError(null);
    const target: "user" | "environment" =
      facingRef.current === "environment" ? "user" : "environment";
    try {
      const stream = await startCameraForFacing(target, currentDeviceIdRef.current);
      stopStream(streamRef.current);
      streamRef.current = stream;
      const settings = stream.getVideoTracks()[0]?.getSettings();
      currentDeviceIdRef.current = settings?.deviceId ?? null;
      // Trust the lens's reported facing when it gives one; otherwise fall back
      // to the intent we targeted (iOS often misreports facingMode, so the intent
      // is the reliable toggle state for the next tap).
      facingRef.current =
        settings?.facingMode === "user" || settings?.facingMode === "environment"
          ? settings.facingMode
          : target;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        void video.play()?.catch(() => {
          /* autoplay can reject silently; the frame still shows */
        });
      }
      void recordDeviceInfo();
      if (diagRef.current) void refreshCameraDiag();
    } catch (err) {
      setCaptureError((err as Error).message);
    }
  }

  // Freeze the frame that was just taken over the viewfinder, flash, and buzz
  // where the platform allows it. Idempotent teardown so no object URL leaks.
  function showShotFeedback(blob: Blob): void {
    clearShotFeedback();
    const url = URL.createObjectURL(blob);
    shotPreviewRef.current = url;
    setShotPreview(url);
    try {
      // No-op on iOS Safari, which exposes no vibrate().
      navigator.vibrate?.(30);
    } catch {
      /* some engines throw outside a gesture context */
    }
  }

  function clearShotFeedback(): void {
    const url = shotPreviewRef.current;
    if (!url) return;
    shotPreviewRef.current = null;
    URL.revokeObjectURL(url);
    setShotPreview(null);
  }

  async function captureCurrent(): Promise<void> {
    const video = videoRef.current;
    if (!video || !currentStep || busy) return;
    setCaptureError(null);
    setBusy(true);
    let snap: HTMLCanvasElement | null = null;
    try {
      // Freeze the frame first, encode after: the shutter used to wait for two
      // JPEG encodes back to back (square, then the 8-12 MP full frame) with the
      // guidance loop still running, which took seconds on a 4 GB Android
      // (preview scan DUQ5U4). Now the tap costs one drawImage, the guidance
      // pauses, and the full frame encodes during the feedback pause below.
      // The 12 MP snapshot and its encode are the allocations an older iPhone
      // can refuse (Safari reports no deviceMemory, so it always gets 4032).
      // Either failing only drops this shot's full frame, which IBV alone
      // needs: the square then comes straight from the video as before.
      const gravity = gravitySamplerRef.current.meanInG();
      encodingRef.current = true;
      try {
        snap = snapshotFrame(video);
      } catch {
        snap = null;
      }
      const blob = await captureSquareJpeg(snap ?? video);
      const fullBlobPending: Promise<Blob | undefined> = snap
        ? captureFullFrameJpeg(snap).catch(() => undefined)
        : Promise.resolve(undefined);
      // Freeze the rolling substrate window at the shutter (see
      // `substrateTelemetry`): framing is settled here, so the numbers describe
      // the floor rather than the customer re-aiming between shots.
      const shotSubstrate = summariseSubstrateWindow(substrateWindowRef.current);
      if (shotSubstrate) substrateAtShotRef.current[currentStep.filename] = shotSubstrate;
      if (video.videoWidth && video.videoHeight) {
        cropFractionRef.current = sensorCropFraction(video.videoWidth, video.videoHeight);
      }
      // Record the tilt at shot time, like the prototype's per-photo `angle`
      // (null when the device exposes no orientation → omitted from the meta).
      const shotAngle = angle?.value ?? null;
      // Shutter feedback, then the pause, and only then the state that moves
      // the flow on. `setResults` used to run here, BEFORE the pause: the step
      // pill, the title and the guide outline all jumped to the next step the
      // instant the shutter was tapped, 800 ms ahead of the card explaining it.
      // Freezing the captured frame and holding the step until the pause is over
      // makes the shot itself the visible event instead.
      showShotFeedback(blob);
      const [fullBlob] = await Promise.all([
        fullBlobPending,
        new Promise((resolve) => setTimeout(resolve, POST_CAPTURE_DELAY_MS)),
      ]);
      const next = sortByStep([
        ...results,
        { step: currentStep, blob, fullBlob, gravity, angle: shotAngle },
      ]);
      clearShotFeedback();
      setResults(next);

      // Re-taking one foot from the review screen: once its 3 angles are back,
      // go straight to review — no "foot still?" confirm, since the customer
      // deliberately re-placed the foot for this retake.
      if (retakeSide) {
        const sideDone = next.filter((r) => r.step.side === retakeSide).length === 3;
        if (sideDone) {
          stopStream(streamRef.current);
          streamRef.current = null;
          setRetakeSide(null);
          setModal(null);
          setPhase("review");
          return;
        }
        setModal("instruction");
        return;
      }

      // Prototype cadence: after a foot's 3rd shot → confirm; else → the next
      // step card. Batched with the `setResults` above, so the step the card
      // describes and the step the viewfinder shows change in one commit.
      if (next.length === 3 || next.length === CAPTURE_STEPS.length) {
        setModal("confirm");
      } else {
        setModal("instruction");
      }
    } catch (err) {
      clearShotFeedback();
      setCaptureError((err as Error).message);
    } finally {
      if (snap) releaseSnapshot(snap);
      encodingRef.current = false;
      setBusy(false);
    }
  }

  // "Foot still?" → Yes: continue to the next foot / review. No: redo this foot.
  function confirmFootStill(kept: boolean): void {
    const finishedRight = results.length === CAPTURE_STEPS.length;
    if (kept) {
      if (finishedRight) {
        stopStream(streamRef.current);
        streamRef.current = null;
        setModal(null);
        setPhase("review");
      } else {
        setModal("instruction"); // move on to the first right-foot step
      }
    } else {
      // Drop this foot's captures and re-show its first step card.
      setResults(finishedRight ? results.slice(0, 3) : []);
      setModal("instruction");
    }
  }

  // Re-take one whole foot (its 3 angles) from the review screen. Single-angle
  // retakes aren't offered on purpose: the foot must not move between a foot's
  // three shots, so the smallest safe redo unit is the whole foot. We drop that
  // foot's captures (keeping the other foot), re-open the camera — it was
  // stopped on entering review — and re-enter the capture flow at that foot's
  // first gap. `requestCamera` sets phase → capturing + shows the step card.
  async function retakeFoot(side: Side): Promise<void> {
    setResults((prev) => sortByStep(prev.filter((r) => r.step.side !== side)));
    setRetakeSide(side);
    setCaptureError(null);
    await requestCamera();
  }

  function restart(): void {
    stopStream(streamRef.current);
    streamRef.current = null;
    setResults([]);
    substrateAtShotRef.current = {};
    sessionAccRef.current = newSessionTelemetry();
    sessionTrailRef.current = [];
    setRetakeSide(null);
    setCaptureError(null);
    setModal(null);
    setPhase("consent");
    setAgreed(false);
  }

  /**
   * Export the diag telemetry without completing a scan. The sidecar path only
   * runs after all 6 shots, so a floor the guidance never clears — the very case
   * the substrate advisories exist for — would otherwise upload nothing at all.
   * This writes the same blocks to a file (and the clipboard) so an unshootable
   * surface still yields its numbers.
   */
  async function saveTelemetry(): Promise<void> {
    const payload = {
      version: APP_VERSION,
      scanID: session.scanID,
      at: new Date().toISOString(),
      completedShots: results.length,
      camera: cameraDiag ? diagMetaFrom(cameraDiag, guidanceOutcomeRef.current) : null,
      session: sessionTelemetry(sessionAccRef.current, sessionTrailRef.current) ?? null,
      substrate:
        substrateTelemetry(
          results.map((r) => ({
            filename: r.step.filename,
            summary: substrateAtShotRef.current[r.step.filename] ?? null,
          })),
        ) ?? null,
    };
    const json = JSON.stringify(payload, null, 2);
    // Clipboard first (fastest path back to a chat), download second (survives a
    // clipboard permission refusal). Both best-effort — neither must throw into
    // the tap handler.
    try {
      await navigator.clipboard?.writeText(json);
    } catch {
      /* clipboard unavailable / denied — the download below still lands */
    }
    try {
      const url = URL.createObjectURL(new Blob([json], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `hezo-diag-${session.scanID}-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* download blocked — the clipboard copy above is the fallback */
    }
    setTelemetrySaved(true);
    setTimeout(() => setTelemetrySaved(false), 2000);
  }

  function upload(): void {
    // 6 photos + the paper-format sidecar (backend uses it later). Every scan
    // carries the `device` block (phone + rear-lens facts); the far more
    // detailed `diag` snapshot rides along only when diag mode is on.
    // Substrate telemetry replaces the screenshot workflow for threshold
    // calibration: the per-shot window summaries ride the sidecar's free-form
    // `diag` key straight to S3. Diag-gated like the rest of that block, so
    // normal customer scans keep the clean two-field sidecar.
    const substrate = diag
      ? substrateTelemetry(
          results.map((r) => ({
            filename: r.step.filename,
            summary: substrateAtShotRef.current[r.step.filename] ?? null,
          })),
        )
      : undefined;
    const sessionBlock = diag
      ? sessionTelemetry(sessionAccRef.current, sessionTrailRef.current)
      : undefined;
    const camera =
      diag && cameraDiag ? diagMetaFrom(cameraDiag, guidanceOutcomeRef.current) : undefined;
    const extra = {
      ...(substrate ? { substrate } : {}),
      ...(sessionBlock ? { session: sessionBlock } : {}),
    };
    const diagMeta =
      camera || Object.keys(extra).length ? { ...camera, ...extra } : undefined;
    // Required files first, the optional full frames last: the upload pool
    // works in this order, so a slow uplink spends the presigned URLs' 600 s
    // on what the solve needs (see UPLOAD_RETRIES).
    const shots = captureResultsToInputs(results);
    onComplete(
      [
        ...shots.filter((i) => !isOptionalUpload(i.filename)),
        paperMetaInput(
          paperFormat,
          cropFractionRef.current ?? undefined,
          captureAngles(results),
          diagMeta,
          deviceInfoRef.current,
          captureGravity(results),
        ),
        ...shots.filter((i) => isOptionalUpload(i.filename)),
      ],
      captureLabel,
    );
  }

  // The foot the confirm dialog is asking about (derived from progress).
  const confirmFoot: Side = results.length <= 3 ? "left" : "right";

  return (
    <div className="space-y-5" data-testid="capture-flow" data-capture-phase={phase}>
      {/* Operator/debug context — noise for real customers, so DEBUG-only. */}
      {DEBUG && <SessionLine session={session} />}

      {phase === "consent" && (
        <ConsentScreen
          scanID={run ? null : session.scanID}
          agreed={agreed}
          setAgreed={setAgreed}
          paperFormat={paperFormat}
          setPaperFormat={setPaperFormat}
          onStart={startFromConsent}
          crashNotice={crashNoticeDue()}
          onSimulate={onSimulate && (() => onSimulate(captureLabel))}
          captureLabel={devCapture ? captureLabel : null}
          setCaptureLabel={(v) => {
            setCaptureLabel(v);
            storeCaptureLabel(v);
          }}
        />
      )}

      {phase === "prep" && (
        <PrepScreen
          onStart={() => void requestCamera()}
          onBack={() => {
            // Consent stays ticked: going back is for the paper format, not a restart.
            setPhase("consent");
            window.scrollTo?.(0, 0);
          }}
        />
      )}

      {phase === "resume" && run && (
        <ResumeRunScreen run={run} onStart={() => void requestCamera()} />
      )}

      {phase === "starting" && <CameraStarting />}

      {phase === "denied" && (
        <DeniedScreen
          error={startError}
          onRetry={requestCamera}
          onSimulate={onSimulate && (() => onSimulate(captureLabel))}
        />
      )}

      {phase === "capturing" && (
        <div data-testid="capture-viewfinder-wrap">
          {/* Rendered unconditionally: after the 6th shot there is no next
              step, and gating this on `currentStep` unmounted the whole camera
              view while the confirm dialog was up: a blank screen for the
              length of the pause, and a dead <video> if the answer sent the
              customer back in. `viewStep` falls back to the last step. */}
          <CaptureViewfinder
            run={run}
            attachVideo={attachVideo}
            overlayRef={overlayRef}
            step={viewStep}
            stepNumber={viewStepNumber}
            guidanceStatus={guidanceStatus}
            banner={banner}
            advisory={advisory}
            angle={angle}
            captureError={captureError}
            lowResolution={lowResolution}
            capturable={shutterEnabled(
              modal !== null ||
                busy ||
                lowResolution ||
                !currentStep,
              banner?.severity ?? null,
            )}
            shotPreview={shotPreview}
            onCapture={captureCurrent}
            onSwitchCamera={() => void switchCamera()}
            onResolutionCheck={evaluateResolution}
            diag={diag}
            cameraDiag={cameraDiag}
            guidanceOutcome={guidanceOutcome}
            lensStats={lensStats}
            lensBlocked={lensBlocked}
            shadowStats={shadowStats}
            substrateStats={substrateStats}
            substrateSummary={substrateSummary}
            telemetrySaved={telemetrySaved}
            onSaveTelemetry={() => void saveTelemetry()}
            onLogoTap={onLogoTap}
            onDisableDiag={() => setDiagMode(false)}
          />

          {modal === "instruction" && currentStep && (
            <StepInstructionModal
              step={currentStep}
              stepNumber={stepIndex + 1}
              onStart={() => setModal(null)}
              runHint={startMode === "auto" && stepIndex === 0 && retakeSide === null && !!run}
            />
          )}
          {modal === "confirm" && (
            <ConfirmStillModal foot={confirmFoot} onAnswer={confirmFootStill} />
          )}
        </div>
      )}

      {phase === "review" && (
        <ReviewScreen
          results={results}
          nextRun={run && run.index < run.total ? run.index + 1 : null}
          onUpload={upload}
          onRetakeFoot={(side) => void retakeFoot(side)}
          onRestart={restart}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────

export function CameraStarting(): React.ReactElement {
  const { t } = useI18n();
  return (
    <p className="text-sm text-slate-500 animate-pulse" data-testid="camera-starting">
      {t("banner.requestingCamera")}
    </p>
  );
}

function SessionLine({ session }: { session: ScanSession }): React.ReactElement {
  return (
    <section>
      <h2 className="text-lg font-semibold">Foot scan</h2>
      <p className="text-sm text-slate-600">
        customer: <code className="font-mono text-xs">{session.customerId.slice(0, 8)}…</code>{" "}
        · scan: <code className="font-mono text-xs">{session.scanID}</code>
      </p>
    </section>
  );
}

// The capture mistakes that most often fail on the server (footscan-server#126:
// ankle hidden by clothing, jewellery, a sheet moved between shots, hard sun),
// each with a right and a wrong sketch from src/assets/checklist/. Two cards of two,
// so each card fits a phone screen without scrolling.
const PREP_CARDS = [
  [
    { id: "legs", text: "consent.checkLegs" },
    { id: "jewelry", text: "consent.checkJewelry" },
  ],
  [
    { id: "sheet", text: "consent.checkSheet" },
    { id: "light", text: "consent.checkLight" },
  ],
] as const satisfies readonly (readonly { id: string; text: TransKey }[])[];

// Bundled through Vite, not served from public/: public/ files keep their name
// and the CDN marks them immutable for a year, so a redrawn sketch would never
// reach a phone that has seen the old one.
const PREP_SKETCHES = import.meta.glob<string>("../assets/checklist/*.svg", {
  eager: true,
  query: "?url",
  import: "default",
});

export function prepSketchUrl(id: string, kind: "ok" | "bad"): string | undefined {
  return PREP_SKETCHES[`../assets/checklist/${id}-${kind}.svg`];
}

// Matches the track's `gap-3`; the scroll offset of card n is n × (width + gap).
const PREP_CARD_GAP_PX = 12;

function PrepCard({ index }: { index: number }): React.ReactElement {
  const { t } = useI18n();
  return (
    <section
      className={`${PANEL} w-full shrink-0 snap-center space-y-4 text-sm text-slate-300`}
      data-testid={`prep-card-${index + 1}`}
    >
      <h3 className="flex justify-between font-semibold text-white">
        {t("consent.checklistTitle")}
        <span className="font-medium tabular-nums text-slate-500" dir="ltr">
          {index + 1}/{PREP_CARDS.length}
        </span>
      </h3>
      {PREP_CARDS[index].map(({ id, text }) => (
        <div key={id} className="space-y-2" data-testid={`prep-check-${id}`}>
          <p>{t(text)}</p>
          <div className="grid grid-cols-2 gap-2">
            {(["ok", "bad"] as const).map((kind) => (
              <figure key={kind} className="space-y-1">
                <img
                  src={prepSketchUrl(id, kind)}
                  alt=""
                  width={160}
                  height={120}
                  className="h-auto w-full rounded-lg"
                />
                <figcaption
                  className={`text-center text-xs font-medium ${
                    kind === "ok" ? "text-emerald-400" : "text-rose-400"
                  }`}
                >
                  {t(kind === "ok" ? "consent.checkGood" : "consent.checkBad")}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

/**
 * Between consent and camera: the checklist as swipeable cards. The primary
 * button pages to the next card and only starts the camera on the last one,
 * so every customer has seen all four points; "back" pages back, then leaves.
 */
export function PrepScreen({
  onStart,
  onBack,
  initialCard = 0,
}: {
  onStart: () => void;
  onBack: () => void;
  /** Only the copy preview opens on a later card. */
  initialCard?: number;
}): React.ReactElement {
  const { t, dir } = useI18n();
  const [card, setCard] = useState(initialCard);
  const trackRef = useRef<HTMLDivElement>(null);
  // Card a button is scrolling to: while set, scroll events are that animation,
  // not a swipe, and must not move `card`. Cleared on arrival or by timeout
  // (no scroll event at all when the track is already there).
  const pagingRef = useRef<{ to: number; timer: ReturnType<typeof setTimeout> } | null>(null);
  const last = card === PREP_CARDS.length - 1;

  const step = (): number => (trackRef.current?.clientWidth ?? 0) + PREP_CARD_GAP_PX;
  useEffect(() => {
    if (trackRef.current && initialCard) trackRef.current.scrollLeft = initialCard * step();
  }, [initialCard]);

  useEffect(() => () => clearTimeout(pagingRef.current?.timer), []);

  function goTo(n: number): void {
    setCard(n);
    clearTimeout(pagingRef.current?.timer);
    pagingRef.current = { to: n, timer: setTimeout(() => (pagingRef.current = null), 1000) };
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    trackRef.current?.scrollTo?.({ left: n * step(), behavior: reduce ? "auto" : "smooth" });
  }

  return (
    <div className="space-y-4" data-testid="capture-prep" data-prep-card={card + 1}>
      <div
        ref={trackRef}
        dir="ltr"
        onScroll={(e) => {
          // A swipe counts once it has snapped; offsets in between would flip
          // the buttons back and forth.
          const w = step();
          if (w <= PREP_CARD_GAP_PX) return;
          const pos = e.currentTarget.scrollLeft / w;
          const n = Math.round(pos);
          if (Math.abs(pos - n) >= 0.02) return;
          const paging = pagingRef.current;
          if (paging) {
            if (n !== paging.to) return;
            clearTimeout(paging.timer);
            pagingRef.current = null;
          }
          setCard(n);
        }}
        className="flex snap-x snap-mandatory gap-3 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="prep-checklist"
      >
        {PREP_CARDS.map((_, i) => (
          // The track is pinned LTR so scrollLeft means the same in Arabic;
          // each card gets the page's own direction back.
          <div key={i} className="contents" dir={dir}>
            <PrepCard index={i} />
          </div>
        ))}
      </div>
      <div className="flex justify-center gap-2" dir="ltr" aria-hidden="true">
        {PREP_CARDS.map((_, i) => (
          <span
            key={i}
            className={`h-2 rounded-full transition-all motion-reduce:transition-none ${
              i === card ? "w-6 bg-white" : "w-2 bg-slate-600"
            }`}
          />
        ))}
      </div>
      {last ? (
        <button type="button" onClick={onStart} data-testid="start-camera-button" className={PRIMARY_BTN}>
          {t("init.startScan")}
        </button>
      ) : (
        <button type="button" onClick={() => goTo(card + 1)} data-testid="prep-next-button" className={PRIMARY_BTN}>
          {t("consent.continue")}
        </button>
      )}
      <button
        type="button"
        onClick={() => (card > 0 ? goTo(card - 1) : onBack())}
        data-testid="prep-back-button"
        className={SECONDARY_BTN}
      >
        {t("consent.back")}
      </button>
    </div>
  );
}

export function ConsentScreen({
  scanID,
  agreed,
  setAgreed,
  paperFormat,
  setPaperFormat,
  onStart,
  crashNotice = false,
  onSimulate,
  captureLabel,
  setCaptureLabel,
}: {
  /** Null on a multi-run invite: one run's scanID is one of 2N. */
  scanID: string | null;
  agreed: boolean;
  setAgreed: (v: boolean) => void;
  paperFormat: PaperFormat;
  setPaperFormat: (f: PaperFormat) => void;
  onStart: () => void;
  /** The tab died during the last capture: say why the photos are gone. */
  crashNotice?: boolean;
  onSimulate?: () => void;
  /** Null outside dev capture — the field is then not rendered at all. */
  captureLabel: string | null;
  setCaptureLabel: (v: string) => void;
}): React.ReactElement {
  const { t } = useI18n();
  return (
    <div className="space-y-4" data-testid="capture-consent">
      {scanID && (
        <p className="text-xs text-slate-500" data-testid="scan-id">
          {t("app.scanId")}:{" "}
          <code className="font-mono tracking-wider text-slate-300">{scanID}</code>
        </p>
      )}
      {crashNotice && (
        <p
          role="status"
          data-testid="crash-notice"
          className="rounded-xl border border-amber-300/40 bg-amber-300/10 px-3 py-2 text-sm text-amber-200"
        >
          {t("consent.crashNotice")}
        </p>
      )}
      <section className={`${PANEL} space-y-2 text-sm text-slate-300`}>
        <h3 className="font-semibold text-white">{t("consent.beforeYouStart")}</h3>
        <p>{t("init.description")}</p>
        {/* photosIntro carries <strong> from our own bundled translations. */}
        <p dangerouslySetInnerHTML={{ __html: t("consent.photosIntro", { format: paperFormat }) }} />
        <p>
          {t("consent.gdprBefore")}
          <a
            href="https://www.hezo-cycling.com/policies/privacy-policy"
            target="_blank"
            rel="noreferrer"
            className="font-medium underline"
          >
            {t("consent.privacyPolicy")}
          </a>
          .
        </p>
      </section>

      {/* Paper format — the choice rides to the backend in capture-meta.json
          (footscan-server's pose still assumes A4 today; Letter is a follow-up). */}
      <section className="space-y-1">
        <span className="text-xs font-medium text-slate-400">{t("consent.formatLabel")}</span>
        <div className="flex gap-2" role="group" aria-label={t("consent.formatLabel")}>
          {PAPER_FORMATS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setPaperFormat(f)}
              data-testid={`format-${f}`}
              aria-pressed={paperFormat === f}
              className={`flex-1 rounded-full border py-2 text-sm font-medium transition ${
                paperFormat === f
                  ? "border-white bg-white text-slate-900"
                  : "border-white/15 text-slate-300 hover:bg-white/5"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </section>

      {/* Dev capture only: the note that groups this run with the same foot
          captured in tools/proto-ab. Hardcoded English like the rest of the
          dev-capture surface — no customer ever sees it. */}
      {captureLabel !== null && (
        <section className="space-y-1">
          <label
            htmlFor="capture-label"
            className="text-xs font-medium text-slate-400"
          >
            dev: who/what is this scan? (groups it with the proto-ab run)
          </label>
          <input
            id="capture-label"
            type="text"
            value={captureLabel}
            onChange={(e) => setCaptureLabel(e.target.value)}
            placeholder="e.g. seb-left-a4"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            data-testid="capture-label-input"
            className="w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 font-mono text-sm text-slate-100 placeholder:text-slate-500"
          />
        </section>
      )}

      <label className="flex items-start gap-2 text-sm text-slate-300">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-0.5"
          data-testid="consent-checkbox"
        />
        <span>{t("consent.checkbox")}</span>
      </label>

      <button
        type="button"
        onClick={onStart}
        disabled={!agreed}
        data-testid="consent-continue-button"
        className={PRIMARY_BTN}
      >
        {t("consent.continue")}
      </button>

      {DEBUG && onSimulate && (
        <button
          type="button"
          onClick={onSimulate}
          data-testid="simulate-button"
          className="w-full rounded-full border border-white/15 py-2 text-xs text-slate-400 hover:bg-white/5"
        >
          debug: simulate scan (synthetic bytes, no camera)
        </button>
      )}
    </div>
  );
}

export function DeniedScreen({
  error,
  onRetry,
  onSimulate,
}: {
  error: string | null;
  onRetry: () => void;
  onSimulate?: () => void;
}): React.ReactElement {
  const { t } = useI18n();
  return (
    <div className="space-y-4" data-testid="camera-denied">
      <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4">
        <h3 className="font-semibold text-rose-200">{t("denied.title")}</h3>
        <p className="mt-1 text-sm text-rose-100/90">{t("denied.body")}</p>
        {error && (
          <p className="mt-2 break-words font-mono text-xs text-rose-200/70">{error}</p>
        )}
      </div>
      <button type="button" onClick={onRetry} className={PRIMARY_BTN}>
        {t("denied.retry")}
      </button>
      {DEBUG && onSimulate && (
        <button
          type="button"
          onClick={onSimulate}
          data-testid="simulate-button"
          className="w-full rounded-full border border-white/15 py-2 text-xs text-slate-400 hover:bg-white/5"
        >
          debug: simulate scan (synthetic bytes, no camera)
        </button>
      )}
    </div>
  );
}

export function CaptureViewfinder({
  attachVideo,
  overlayRef,
  step,
  stepNumber,
  shotPreview,
  guidanceStatus,
  banner,
  advisory,
  angle,
  captureError,
  lowResolution,
  capturable,
  onCapture,
  onSwitchCamera,
  onResolutionCheck,
  diag,
  cameraDiag,
  guidanceOutcome,
  lensStats,
  lensBlocked,
  shadowStats,
  substrateStats,
  substrateSummary,
  telemetrySaved,
  onSaveTelemetry,
  onLogoTap,
  onDisableDiag,
  run = null,
}: {
  attachVideo: (el: HTMLVideoElement | null) => void;
  overlayRef: React.RefObject<HTMLCanvasElement | null>;
  step: CaptureStep;
  stepNumber: number;
  /** Frozen frame of the shot just taken (object URL), or null. */
  shotPreview: string | null;
  guidanceStatus: GuidanceStatus;
  banner: GuidanceMessage | null;
  advisory: GuidanceMessage | null;
  angle: AngleReading | null;
  captureError: string | null;
  lowResolution: boolean;
  capturable: boolean;
  onCapture: () => void;
  onSwitchCamera: () => void;
  onResolutionCheck: () => void;
  diag: boolean;
  cameraDiag: CameraDiagSnapshot | null;
  /** EP the guidance session took, or the error that left it unavailable. */
  guidanceOutcome: GuidanceOutcome;
  lensStats: OcclusionAnalysis | null;
  lensBlocked: boolean;
  shadowStats: PaperShadowStats | null;
  substrateStats: SubstrateStats | null;
  substrateSummary: SubstrateWindowSummary | null;
  telemetrySaved: boolean;
  onSaveTelemetry: () => void;
  onLogoTap: () => void;
  onDisableDiag: () => void;
  /** Scan run of a multi-run invite, shown above the step. */
  run?: RunInfo | null;
}): React.ReactElement {
  const { t } = useI18n();
  // Full-bleed immersive viewfinder (prototype styling): the camera fills the
  // screen with the guide + mask overlays, glassy pills for step + angle, the
  // guidance banner up top, and a single capture button pinned to the bottom.
  // `fixed inset-0` lifts it out of the app's card layout.
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center overflow-hidden bg-black"
      data-testid="capture-viewfinder"
    >
      {/* Square capture window — the archived prototype's `.viewport`
          (`width/height: 100vmin`). captureSquareJpeg keeps the centre
          min(vw,vh)² of the frame, so the preview MUST be that same square;
          the port had painted the video full-bleed (`fixed inset-0`) instead,
          which left the guide floating too small inside a portrait feed and the
          foot never lined up (observed on iPhone: 1080×1920 portrait frames).
          Video (object-cover → centre-square), guide, and AI mask all fill this
          one square, so what the customer frames is exactly what's captured.
          Held portrait, the 100vmin square letterboxes top/bottom — those rows
          aren't captured anyway, and the top/bottom UI sits in those bands like
          the prototype.
          The window itself is now only the middle 75% of that square: the 4:3
          landscape crop footscan-server's IBV step cuts from the portrait frame
          (full width, middle 3/4 of the height), so the customer only frames
          what IBV gets. Video, mask and shot preview stay object-cover and thus
          show exactly that band; the uploaded square still holds it whole. */}
      <div
        className="relative h-[75vmin] w-[100vmin] overflow-hidden"
        data-testid="capture-window"
      >
        <video
          ref={attachVideo}
          playsInline
          muted
          autoPlay
          className="absolute inset-0 h-full w-full object-cover"
          data-testid="viewfinder-video"
          onLoadedMetadata={onResolutionCheck}
          onResize={onResolutionCheck}
        />
        {/* Per-step guide outline (prototype #guide-overlay): light SVG
            composited with screen blend so it glows over the camera feed. Square
            SVG in a square window → fills it, aligned with the captured crop.
            In the 4:3 band the square guide keeps its size but moves up so its
            sheet and foot sit centred in the band. */}
        <img
          src={step.guideUrl}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 h-[100vmin] w-full object-contain"
          style={{ opacity: 0.8, mixBlendMode: "screen", top: `${IBV_GUIDE_TOP_VMIN[step.angle]}vmin` }}
          data-testid="guide-overlay"
        />
        {/* ONNX mask outline — the 128² canvas is the frame's centre-square, so
            in the square window it maps 1:1 onto the video + guide (in the IBV
            band, object-cover crops it like the video). */}
        <canvas
          ref={overlayRef}
          width={MODEL_SIZE}
          height={MODEL_SIZE}
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          style={{ opacity: 0.6, imageRendering: "pixelated" }}
          data-testid="guidance-overlay"
        />
        {/* The dashed frame the step copy refers to ("the whole sheet belongs
            inside the dashed frame"): the window edge, i.e. the IBV crop. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 border-2 border-dashed border-amber-300/90"
          data-testid="ibv-crop-frame"
        />
        {/* Post-shutter feedback, on top of everything in the square so what
            the customer sees frozen is exactly what was captured. */}
        {shotPreview && <ShotFeedback key={shotPreview} url={shotPreview} />}
      </div>

      {/* Top overlay: logo + lang, then banner, then step pill + angle. */}
      <div
        className="absolute inset-x-0 top-0 space-y-2 p-3"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 0.5rem)" }}
      >
        {/* HEZO wordmark — mirrors the app header, so the logo is present on
            the full-bleed viewfinder too. Triple-tapping it toggles the hidden
            on-device diagnostics panel (no cable / URL param needed). */}
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onLogoTap}
            aria-label="HEZO"
            data-testid="viewfinder-logo"
            className="rounded-2xl bg-black/55 px-3 py-1.5 backdrop-blur"
          >
            <HezoLogo className="h-5 text-white" />
          </button>
          <LanguageSwitcher variant="compact" dark />
        </div>
        {diag && (
          <DiagPanel
            snap={cameraDiag}
            guidance={guidanceOutcome}
            lensStats={lensStats}
            lensBlocked={lensBlocked}
            shadowStats={shadowStats}
            substrateStats={substrateStats}
            substrateSummary={substrateSummary}
            telemetrySaved={telemetrySaved}
            onSaveTelemetry={onSaveTelemetry}
            onClose={onDisableDiag}
          />
        )}
        {banner && (
          <div
            data-testid="guidance-banner"
            data-severity={banner.severity}
            className={`rounded-xl bg-black/55 px-3 py-2 text-center text-sm font-semibold backdrop-blur ${
              banner.severity === "good" ? "text-emerald-300" : "text-rose-300"
            }`}
          >
            {t(banner.key)}
          </div>
        )}
        {/* Second, non-blocking slot: advisory hints (uneven lighting) shown in
            amber so they read as "worth fixing" without the blocking red. */}
        {advisory && (
          <div
            data-testid="advisory-banner"
            data-severity={advisory.severity}
            className="rounded-xl bg-black/55 px-3 py-2 text-center text-sm font-semibold text-amber-300 backdrop-blur"
          >
            {t(advisory.key)}
          </div>
        )}
        {lowResolution && (
          <div
            data-testid="low-resolution-banner"
            className="rounded-xl bg-black/55 px-3 py-2 text-center text-sm font-semibold text-rose-300 backdrop-blur"
          >
            {t("banner.lowResolution")}
          </div>
        )}
        <div className="flex items-start justify-between gap-2">
          <div className="rounded-2xl bg-black/55 px-3 py-2 text-white backdrop-blur">
            {run && (
              <div
                data-testid="run-progress"
                className="text-[10px] font-semibold uppercase tracking-wide text-amber-200"
              >
                {t("runs.badge", { run: run.index, runs: run.total })}
              </div>
            )}
            <div
              data-testid="step-progress"
              className="text-[10px] font-medium uppercase tracking-wide text-white/60"
            >
              {t("ui.stepOf", { current: stepNumber })}
            </div>
            <div data-testid="step-title" className="text-sm font-semibold">
              {t(step.nameKey as TransKey)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {angle && (
              <div
                data-testid="angle-readout"
                className="rounded-2xl bg-black/55 px-3 py-2 text-right backdrop-blur"
              >
                <div
                  className={`text-lg font-bold leading-none tabular-nums ${
                    angle.good ? "text-emerald-400" : "text-rose-400"
                  }`}
                >
                  {angle.value}°
                </div>
                <div className="text-[10px] uppercase tracking-wide text-white/60">
                  {t("ui.cameraAngle")}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom overlay: guidance status, capture button. */}
      <div
        className="absolute inset-x-0 bottom-0 space-y-2 p-4"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)" }}
      >
        {guidanceStatus === "loading" && (
          <p className="text-center text-xs text-white/60" data-testid="guidance-loading">
            {t("banner.loadingModel")}
          </p>
        )}
        {guidanceStatus === "unavailable" && (
          <p className="text-center text-xs text-white/60" data-testid="guidance-unavailable">
            {t("banner.modelLoadError")}
          </p>
        )}
        {captureError && (
          <p className="text-center text-xs text-rose-300" data-testid="capture-error">
            {captureError}
          </p>
        )}
        {/* Native-camera-style control row: round shutter centered, small flip
            button to its right (empty left cell keeps the shutter centered). */}
        <div className="grid grid-cols-3 items-center">
          <div aria-hidden="true" />
          <div className="flex justify-center">
            <button
              type="button"
              onClick={onCapture}
              disabled={!capturable}
              data-testid="capture-button"
              aria-label={t("ui.capture")}
              className="relative flex h-[72px] w-[72px] items-center justify-center rounded-full transition active:scale-95 disabled:opacity-40"
            >
              <span className="absolute inset-0 rounded-full border-[3px] border-white" />
              <span className="h-14 w-14 rounded-full bg-white shadow-lg" />
            </button>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onSwitchCamera}
              data-testid="switch-camera-button"
              aria-label={t("ui.switchCamera")}
              title={t("ui.switchCamera")}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-black/40 text-white backdrop-blur transition active:bg-black/60"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5"
                aria-hidden="true"
              >
                <path d="M23 4v6h-6" />
                <path d="M1 20v-6h6" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// On-device diagnostics overlay. Screenshot-friendly: mono, high-contrast, and
// carries exactly the facts needed to tune lens selection + guide sizing — the
// active lens label, resolution/crop, and EVERY enumerated lens (so we can see
// whether the device exposes an ultra-wide/telephoto we should prefer/avoid).
// Toggled via the hidden logo triple-tap; the ✕ turns it back off.
function DiagPanel({
  snap,
  guidance,
  lensStats,
  lensBlocked,
  shadowStats,
  substrateStats,
  substrateSummary,
  telemetrySaved,
  onSaveTelemetry,
  onClose,
}: {
  snap: CameraDiagSnapshot | null;
  /** EP the guidance session took, or the error that left it unavailable. */
  guidance: GuidanceOutcome;
  /** Live per-frame occlusion stats from the guidance loop (null while off). */
  lensStats: OcclusionAnalysis | null;
  lensBlocked: boolean;
  /** Live per-frame paper-shadow stats (null while off / no paper visible). */
  shadowStats: PaperShadowStats | null;
  /** Live per-frame substrate-interference stats (null while off / no paper). */
  substrateStats: SubstrateStats | null;
  /** Rolling spread + fire-rates over the last frames (threshold calibration). */
  substrateSummary: SubstrateWindowSummary | null;
  /** Brief "saved" confirmation after an export. */
  telemetrySaved: boolean;
  /** Export the telemetry without completing a scan (unshootable floors). */
  onSaveTelemetry: () => void;
  onClose: () => void;
}): React.ReactElement {
  const short = (v: string | null): string => (v ? `${v.slice(0, 8)}…` : "?");
  // Collapsing hides the readout but leaves diag MODE on, so the substrate
  // telemetry keeps recording into capture-meta.json. That split matters for
  // calibration runs: the panel covers the viewfinder the tester needs to frame
  // the sheet with, but switching diag off entirely would drop the sidecar data
  // the run exists to collect. "✕" still disables diag outright.
  //
  // Collapsed is the DEFAULT: turning diag on is a request to RECORD, not to
  // read numbers mid-capture, and an expanded panel over the viewfinder is what
  // ruins the framing. The header bar stays (build sha + "rec" confirm it took),
  // "▼" expands on demand, and the state survives all 6 shots — the viewfinder
  // stays mounted across steps.
  const [collapsed, setCollapsed] = useState(true);
  return (
    <div
      data-testid="diag-panel"
      className="max-h-[45vh] overflow-auto rounded-xl bg-black/80 px-3 py-2 font-mono text-[10px] leading-snug text-emerald-200 backdrop-blur"
    >
      <div className={`flex items-center justify-between gap-2 ${collapsed ? "" : "mb-1"}`}>
        <span className="font-bold tracking-wide text-emerald-300">
          DIAG · <span className="text-white" data-testid="diag-version">build {APP_VERSION}</span>
          {collapsed && <span className="text-amber-300"> · rec</span>}
        </span>
        <span className="flex items-center gap-1">
          {/* Export without completing a scan. The sidecar only ships after all
              6 shots, so a floor guidance never clears would upload nothing —
              this is the escape hatch for exactly those surfaces. */}
          <button
            type="button"
            onClick={onSaveTelemetry}
            data-testid="diag-save"
            className="rounded bg-white/15 px-2 py-0.5 text-white"
          >
            {telemetrySaved ? "✓" : "save"}
          </button>
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            data-testid="diag-collapse"
            className="rounded bg-white/15 px-2 py-0.5 text-white"
          >
            {collapsed ? "▼" : "▲"}
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="diag-close"
            className="rounded bg-white/15 px-2 py-0.5 text-white"
          >
            ✕
          </button>
        </span>
      </div>
      {collapsed ? null : snap ? (
        <div className="space-y-0.5">
          <div>
            lens: <span className="text-white">{snap.info.label || "(unlabeled)"}</span>
          </div>
          <div>
            facing: {snap.info.facingMode ?? "?"} · id: {short(snap.info.deviceId)}
          </div>
          <div data-testid="diag-resolution">
            res: <span className="text-white">{snap.info.width ?? "?"}×{snap.info.height ?? "?"}</span>{" "}
            · req: {snap.trackRequest} · zoom:{" "}
            {snap.info.zoom != null ? snap.info.zoom.toFixed(2) : "?"} · crop:{" "}
            {snap.cropFraction != null ? snap.cropFraction.toFixed(3) : "?"}
          </div>
          <div>
            ep: {snap.providers} · iOS: {String(snap.ios)} · gpu: {String(snap.webgpu)} · run:{" "}
            <span className="text-white" data-testid="diag-guidance-provider">
              {guidance.provider ?? (guidance.error ? "none" : "…")}
            </span>
            {guidance.inferMs != null && <> · {guidance.inferMs}ms/frame</>}
          </div>
          {guidance.error && (
            <div className="break-words text-rose-300" data-testid="diag-guidance-error">
              ep err: {guidance.error}
            </div>
          )}
          {lensStats && (
            <div data-testid="diag-lens-stats">
              luma: {lensStats.mean.toFixed(1)} · σ: {lensStats.stddev.toFixed(1)} · ∇:{" "}
              {lensStats.gradient.toFixed(2)} · blank:{" "}
              {lensStats.blankTiles.filter(Boolean).length}/{lensStats.blankTiles.length}{" "}
              {/* 4×4 blank-tile map, row-major (▮ = blank) — shows WHERE the
                  covered region sits, so a floor-induced false blank is
                  distinguishable from a finger at a glance. */}
              <span className="text-emerald-200/70">
                {[0, 1, 2, 3]
                  .map((r) =>
                    lensStats.blankTiles
                      .slice(r * 4, r * 4 + 4)
                      .map((b) => (b ? "▮" : "·"))
                      .join(""),
                  )
                  .join("|")}
              </span>{" "}
              · lens:{" "}
              <span className={lensBlocked ? "text-rose-300" : "text-white"}>
                {lensBlocked ? "BLOCKED" : "ok"}
              </span>
            </div>
          )}
          {/* Paper-shadow (uneven-lighting) metric — the tuning target for the
              advisory. Null when no framed sheet is visible ("shadow: —"). */}
          <div data-testid="diag-shadow-stats">
            shadow:{" "}
            {shadowStats ? (
              <>
                mean {shadowStats.mean.toFixed(0)} · σ {shadowStats.stddev.toFixed(1)} · dark{" "}
                {(shadowStats.shadowRatio * 100).toFixed(0)}% ·{" "}
                <span className={shadowStats.uneven ? "text-amber-300" : "text-white"}>
                  {shadowStats.uneven ? "UNEVEN" : "ok"}
                </span>
              </>
            ) : (
              <span className="text-emerald-200/70">—</span>
            )}
          </div>
          {/* Substrate (floor) interference metrics — the tuning target for the
              low-contrast + busy-surface advisories. Δedge = paper↔floor edge
              contrast, sol/rgh = paper-mask solidity/roughness, jit = box
              jitter, spk = discarded-speckle% / component count. Null when no
              framed sheet is visible ("floor: —"). */}
          <div data-testid="diag-substrate-stats">
            floor:{" "}
            {substrateStats ? (
              <>
                Δedge {substrateStats.edgeContrast.toFixed(0)}
                {substrateStats.edgeSamples ? "" : "?"} · sol{" "}
                {substrateStats.solidity.toFixed(2)} · rgh {substrateStats.roughness.toFixed(1)} ·
                jit {substrateStats.jitter.toFixed(2)} · spk{" "}
                {(substrateStats.discardRatio * 100).toFixed(0)}%/{substrateStats.components} ·{" "}
                <span
                  className={
                    substrateStats.lowContrast || substrateStats.busy
                      ? "text-amber-300"
                      : "text-white"
                  }
                >
                  {substrateStats.lowContrast ? "LOWCON" : substrateStats.busy ? "BUSY" : "ok"}
                </span>
              </>
            ) : (
              <span className="text-emerald-200/70">—</span>
            )}
          </div>
          {/* Rolling window over the same signals — THE calibration readout.
              Each triple is p05·p50·p95 across the last frames, so one
              screenshot per floor captures a distribution instead of one lucky
              frame. "fires" is how often each of the five busy-vote signals
              tripped, which is what identifies a single noisy signal on a floor
              that scans fine (drop/re-weight it rather than raising the vote
              count). Fed only while diag is on; "—" until the first samples. */}
          <div data-testid="diag-substrate-window">
            floor~{" "}
            {substrateSummary ? (
              <>
                {substrateSummary.frames}f · Δedge {spreadText(substrateSummary.edgeContrast, 0)} ·
                sol {spreadText(substrateSummary.solidity, 2)} · rgh{" "}
                {spreadText(substrateSummary.roughness, 1)} · jit{" "}
                {spreadText(substrateSummary.jitter, 2)} · spk{" "}
                {spreadText(substrateSummary.discardRatio, 0, 100)}% · cmp{" "}
                {spreadText(substrateSummary.components, 0)}
              </>
            ) : (
              <span className="text-emerald-200/70">—</span>
            )}
          </div>
          <div data-testid="diag-substrate-fires">
            fires:{" "}
            {substrateSummary ? (
              <>
                spk {rateText(substrateSummary.fires.discard)} · cmp{" "}
                {rateText(substrateSummary.fires.components)} · rgh{" "}
                {rateText(substrateSummary.fires.roughness)} · sol{" "}
                {rateText(substrateSummary.fires.solidity)} · jit{" "}
                {rateText(substrateSummary.fires.jitter)} ·{" "}
                <span className={substrateSummary.busyRate ? "text-amber-300" : "text-white"}>
                  BUSY {rateText(substrateSummary.busyRate)}
                </span>{" "}
                ·{" "}
                <span className={substrateSummary.lowContrastRate ? "text-amber-300" : "text-white"}>
                  LOWCON {rateText(substrateSummary.lowContrastRate)}
                </span>
              </>
            ) : (
              <span className="text-emerald-200/70">—</span>
            )}
          </div>
          <div className="pt-0.5">lenses ({snap.lenses.length}):</div>
          {snap.lenses.map((l, i) => (
            <div key={i} className="pl-2 text-white">
              · {l}
            </div>
          ))}
          <div className="break-all pt-0.5 text-emerald-200/70">ua: {snap.ua}</div>
        </div>
      ) : (
        <div className="text-emerald-200/70">collecting…</div>
      )}
    </div>
  );
}

/**
 * "The photo is in the box": the frame just captured, frozen over the
 * viewfinder behind a white flash that fades out. Keyed on the URL by the
 * caller, so the flash replays on every shot.
 */
function ShotFeedback({ url }: { url: string }): React.ReactElement {
  const [flash, setFlash] = useState(true);
  useEffect(() => {
    // One tick at full opacity, then fade: mounting straight at 0.85 makes the
    // flash hit immediately (a camera flash does not fade IN).
    const id = setTimeout(() => setFlash(false), 40);
    return () => clearTimeout(id);
  }, []);
  return (
    <div className="pointer-events-none absolute inset-0" data-testid="shot-feedback">
      <img
        src={url}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div
        className="absolute inset-0 bg-white"
        style={{
          opacity: flash ? 0.85 : 0,
          transition: `opacity ${SHOT_FLASH_MS}ms ease-out`,
        }}
      />
    </div>
  );
}

/**
 * False for the first `MODAL_ARM_DELAY_MS` after mount. Parks a fresh modal's
 * card under `pointer-events-none` for that window, so the tail of a double-tap
 * cannot answer a dialog that appeared between the two taps.
 */
function useArmed(): boolean {
  const [armed, setArmed] = useState(MODAL_ARM_DELAY_MS <= 0);
  useEffect(() => {
    const id = setTimeout(() => setArmed(true), MODAL_ARM_DELAY_MS);
    return () => clearTimeout(id);
  }, []);
  return armed;
}

export function StepInstructionModal({
  step,
  stepNumber,
  onStart,
  runHint = false,
}: {
  step: CaptureStep;
  stepNumber: number;
  onStart: () => void;
  /** First step of run 2+ in the same visit: there is no page in between any
   *  more, so the "put the sheet down again" hint rides on this card. */
  runHint?: boolean;
}): React.ReactElement {
  const { t } = useI18n();
  const armed = useArmed();
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      data-testid="step-modal"
    >
      <div
        data-armed={armed}
        className={`w-full max-w-sm space-y-3 rounded-2xl border border-white/10 bg-slate-900 p-5 shadow-xl ${
          armed ? "" : "pointer-events-none"
        }`}
      >
        <span className="text-xs font-medium uppercase tracking-wide text-brand-400">
          {t("stepDialog.badge")} · {t("ui.stepOf", { current: stepNumber })}
        </span>
        <h3 className="text-lg font-semibold text-white">{t(step.nameKey as TransKey)}</h3>
        {runHint && (
          <p className="text-sm text-amber-100" data-testid="step-modal-run-hint">
            {t("runs.betweenSetupBody")}
          </p>
        )}
        {/* Guide has light strokes — shows on the dark card. */}
        <img
          src={step.guideUrl}
          alt=""
          aria-hidden="true"
          className="mx-auto h-40 w-40 object-contain"
        />
        {/* Instruction values carry <strong> from our own bundled translations. */}
        <p
          className="text-sm text-slate-300"
          dangerouslySetInnerHTML={{ __html: t(step.instructionKey as TransKey) }}
        />
        <button
          type="button"
          onClick={onStart}
          data-testid="step-modal-start"
          className={PRIMARY_BTN}
        >
          {t("stepDialog.gotIt")}
        </button>
      </div>
    </div>
  );
}

export function ConfirmStillModal({
  foot,
  onAnswer,
}: {
  foot: Side;
  onAnswer: (kept: boolean) => void;
}): React.ReactElement {
  const { t } = useI18n();
  const footLabel = t(foot === "left" ? "foot.left" : "foot.right");
  const armed = useArmed();
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      data-testid="confirm-modal"
    >
      <div
        data-armed={armed}
        className={`w-full max-w-sm space-y-3 rounded-2xl border border-white/10 bg-slate-900 p-5 shadow-xl ${
          armed ? "" : "pointer-events-none"
        }`}
      >
        <span className="text-xs font-medium uppercase tracking-wide text-brand-400">
          {t("confirmDialog.badge")}
        </span>
        <h3 className="text-lg font-semibold text-white">{t("confirmDialog.title")}</h3>
        <p
          className="text-sm text-slate-300"
          dangerouslySetInnerHTML={{ __html: t("confirmDialog.body", { foot: footLabel }) }}
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onAnswer(false)}
            data-testid="confirm-no"
            className="flex-1 rounded-full border border-white/15 py-3 text-sm font-medium text-slate-200 hover:bg-white/5"
          >
            {t("confirmDialog.no")}
          </button>
          <button
            type="button"
            onClick={() => onAnswer(true)}
            data-testid="confirm-yes"
            className="flex-1 rounded-full bg-white py-3 text-sm font-semibold text-slate-900 hover:bg-slate-100"
          >
            {t("confirmDialog.yes")}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * A reopened link continuing run k of N: consent and checklist were done in
 * run 1 on this phone. One short screen, because its button is the tap iOS
 * needs before it grants the motion sensors again.
 */
export function ResumeRunScreen({
  run,
  onStart,
}: {
  run: RunInfo;
  onStart: () => void;
}): React.ReactElement {
  const { t } = useI18n();
  return (
    <div className="space-y-4" data-testid="resume-run-screen">
      <h2 className="text-lg font-semibold text-white">
        {t("runs.resumeTitle", { run: run.index, runs: run.total })}
      </h2>
      <p className="text-sm text-slate-300">{t("runs.resumeBody")}</p>
      <button
        type="button"
        onClick={onStart}
        className={PRIMARY_BTN}
        data-testid="resume-run-start"
      >
        {t("init.startScan")}
      </button>
    </div>
  );
}

export function ReviewScreen({
  results,
  nextRun,
  onUpload,
  onRetakeFoot,
  onRestart,
}: {
  results: CaptureResult[];
  /** Multi-run invite before its last run: the button leads on to this run.
   *  The photos still go up now (a reopened link resumes from them), the
   *  customer just reads "next run" instead of "upload". */
  nextRun?: number | null;
  onUpload: () => void;
  onRetakeFoot: (side: Side) => void;
  onRestart: () => void;
}): React.ReactElement {
  const { t } = useI18n();
  const thumbs = useMemo(
    () => results.map((r) => ({ key: r.step.id, url: URL.createObjectURL(r.blob), step: r.step })),
    [results],
  );
  useEffect(() => {
    return () => thumbs.forEach((tb) => URL.revokeObjectURL(tb.url));
  }, [thumbs]);

  return (
    <div className="space-y-4" data-testid="capture-review">
      <h3 className="text-base font-semibold text-white">{t("ui.scanningComplete")}</h3>
      <p className="text-sm text-slate-300">{t("ui.reviewCaptures")}</p>

      <div className="grid grid-cols-3 gap-2">
        {thumbs.map((tb) => (
          <figure key={tb.key} className="space-y-1">
            <img
              src={tb.url}
              alt={t(tb.step.nameKey as TransKey)}
              className="aspect-square w-full rounded-lg border border-white/10 object-cover"
            />
            <figcaption className="text-[10px] leading-tight text-slate-400">
              {t(tb.step.nameKey as TransKey)}
            </figcaption>
          </figure>
        ))}
      </div>

      <button
        type="button"
        onClick={onUpload}
        data-testid="capture-upload-button"
        className={PRIMARY_BTN}
      >
        {nextRun ? t("runs.reviewNext", { run: nextRun }) : t("ui.uploadToHEZO")}
      </button>

      {/* Per-foot retake: a foot's 3 angles are shot with the foot held still, so
          the smallest safe redo unit is a whole foot (not a single angle). Redoing
          one foot keeps the other foot's shots. */}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => onRetakeFoot("left")}
          data-testid="retake-left"
          className={SECONDARY_BTN}
        >
          {t("ui.retakeFoot", { foot: t("foot.left") })}
        </button>
        <button
          type="button"
          onClick={() => onRetakeFoot("right")}
          data-testid="retake-right"
          className={SECONDARY_BTN}
        >
          {t("ui.retakeFoot", { foot: t("foot.right") })}
        </button>
      </div>

      <button
        type="button"
        onClick={onRestart}
        data-testid="capture-restart-button"
        className="w-full py-2 text-sm font-medium text-slate-400 underline-offset-2 hover:underline"
      >
        {t("ui.restartScan")}
      </button>
    </div>
  );
}
